import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Empty,
  Text,
  Link,
  LayerCard,
  Loader,
  Select,
  Tabs,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { Link as RouterLink } from 'react-router-dom'
import {
  CameraIcon,
  EnvelopeSimpleIcon,
  HouseIcon,
  MapPinIcon,
  NavigationArrowIcon,
  PhoneIcon,
  UserIcon,
} from '@phosphor-icons/react'
import { ApiError, apiRequest } from '../api/client'
import { getMyProfile, updateMyProfile, type UpdateCoworkerProfileInput } from '../api/coworkers'
import {
  ALL_COWORKER_EDIT_FIELDS,
  CoworkerEditForm,
  CoworkerFacePhotoSection,
  useCoworkerPhotoHandlers,
  type CoworkerEditFormPatch,
  type CoworkerEditFormValue,
} from '../components/coworker/CoworkerEditForm'
import { useTranslation, type Locale } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { useRoles } from '../auth/useRoles'
import { useMyFaceStatus } from '../hooks/useMyFaceStatus'
import { FaceStatusBanner, RETAKE_PHOTO_PATH } from '../components/face-status/FaceStatusBanner'
import type { Building, Coworker, Room } from '@hub-wifi/shared'

interface MyProfilePageProps {
  darkMode: boolean
  onToggleDarkMode: () => void
}

interface BuildingWithRooms extends Building {
  rooms: Room[]
}

interface MyBuildingResponse {
  building: BuildingWithRooms | null
}

function toFormValue(coworker: Coworker): CoworkerEditFormValue {
  return {
    name: coworker.name,
    email: coworker.email,
    mobilePhone: coworker.mobilePhone,
    cpf: coworker.cpf,
    taxIdCountry: coworker.taxIdCountry,
    taxIdType: coworker.taxIdType,
    taxIdValue: coworker.taxIdValue,
    birthDate: coworker.birthDate,
  }
}

/** Self-service profile page — route `/my/profile` (role 'user'). Merges the
 * former read-only `/profile` page ("my building" card, roles, dark mode
 * toggle — moved here so there is a single canonical profile destination,
 * see `/profile` -> `/my/profile` redirect in App.tsx) with the editable
 * coworker form, organised into three tabs: Profile (form + building + roles,
 * default), Face photo, and Preferences (language + appearance). Loads and
 * saves the caller's own coworker record via the `/api/coworkers/me`
 * self-service endpoints, reusing the shared `CoworkerEditForm` (rendered
 * without `coworkerId`/`photoHandlers` here — the face-photo section is
 * rendered standalone on its own tab via the exported
 * `CoworkerFacePhotoSection` + `useCoworkerPhotoHandlers`, to avoid
 * duplicating Name/Email between a read-only row and the form). There is no
 * company/status field here regardless of `fields` — the form never renders
 * those. A caller with no linked coworker record (404 from `getMyProfile`)
 * sees a friendly empty state instead of a crash. */
export function MyProfilePage({ darkMode, onToggleDarkMode }: MyProfilePageProps) {
  const { getAccessTokenSilently } = useAuth0()
  const { t, locale, setLocale } = useTranslation()
  const toasts = useKumoToastManager()
  const roles = useRoles()
  const faceStatus = useMyFaceStatus()

  const [activeTab, setActiveTab] = useState('profile')
  const [coworker, setCoworker] = useState<Coworker | null>(null)
  const [loading, setLoading] = useState(true)
  const [notLinked, setNotLinked] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [building, setBuilding] = useState<BuildingWithRooms | null>(null)

  const photoHandlers = useCoworkerPhotoHandlers(coworker?.id)

  useEffect(() => {
    async function fetchData() {
      try {
        const token = await getAccessTokenSilently()
        const result = await getMyProfile(token)
        setCoworker(result)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setNotLinked(true)
        } else {
          setError(err instanceof ApiError ? err.message : t('common.error'))
        }
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [getAccessTokenSilently, t])

  useEffect(() => {
    async function loadBuilding() {
      try {
        const token = await getAccessTokenSilently()
        const res = await apiRequest<MyBuildingResponse>('/api/my/building', { token })
        setBuilding(res.building)
      } catch {
        // silently ignore — building section just won't show
      }
    }
    void loadBuilding()
  }, [getAccessTokenSilently])

  const handleSave = useCallback(
    async (patch: CoworkerEditFormPatch) => {
      try {
        const token = await getAccessTokenSilently()
        const body: UpdateCoworkerProfileInput = patch
        const updated = await updateMyProfile(body, token)
        setCoworker(updated)
        toasts.add({ variant: 'success', title: t('myProfile.updateSuccess') })
      } catch (err) {
        toasts.add({
          variant: 'error',
          title: t('common.errorTitle'),
          description: err instanceof ApiError ? err.message : t('common.error'),
        })
      }
    },
    [getAccessTokenSilently, t, toasts],
  )

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('myProfile.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('myProfile.title')}
        description={t('myProfile.description')}
      />

      <div className="max-w-2xl">
        <FaceStatusBanner status={faceStatus} />
      </div>

      <div className="flex flex-col gap-6 max-w-2xl">
        <Tabs
          tabs={[
            { value: 'profile', label: t('myProfile.tabs.profile') },
            { value: 'facePhoto', label: t('myProfile.tabs.facePhoto') },
            { value: 'preferences', label: t('myProfile.tabs.preferences') },
          ]}
          value={activeTab}
          onValueChange={setActiveTab}
        />

        {activeTab === 'profile' && (
          <div className="flex flex-col gap-6">
            {loading && (
              <div className="flex items-center justify-center py-16">
                <Loader size="lg" />
              </div>
            )}

            {!loading && notLinked && (
              <Empty
                icon={<UserIcon size={48} className="text-kumo-inactive" />}
                title={t('myProfile.notLinkedTitle')}
                description={t('myProfile.notLinkedDescription')}
              />
            )}

            {!loading && !notLinked && error && (
              <Banner variant="error">
                {t('common.errorPrefix')} {error}
              </Banner>
            )}

            {!loading && !notLinked && !error && coworker && (
              <>
                <LayerCard>
                  <LayerCard.Primary>
                    <CoworkerEditForm
                      value={toFormValue(coworker)}
                      onSave={handleSave}
                      fields={ALL_COWORKER_EDIT_FIELDS}
                    />
                  </LayerCard.Primary>
                </LayerCard>

                {building && (
                  <LayerCard>
                    <LayerCard.Primary>
                      <div className="space-y-4">
                        <Text as="h2" variant="heading2">
                          {t('profile.myBuilding')}
                        </Text>
                        <div>
                          <label className="text-xs font-medium text-kumo-subtle block mb-1">
                            {building.name}
                          </label>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPinIcon size={14} className="text-kumo-subtle mt-0.5" />
                          <Text size="sm">{building.address}</Text>
                        </div>
                        {building.phone && (
                          <div className="flex items-center gap-2">
                            <PhoneIcon size={14} className="text-kumo-subtle" />
                            <Link href={`tel:${building.phone}`} className="text-sm">
                              {building.phone}
                            </Link>
                          </div>
                        )}
                        {building.email && (
                          <div className="flex items-center gap-2">
                            <EnvelopeSimpleIcon size={14} className="text-kumo-subtle" />
                            <Link href={`mailto:${building.email}`} className="text-sm">
                              {building.email}
                            </Link>
                          </div>
                        )}
                        {building.googleMapsUrl && (
                          <Link
                            href={building.googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium"
                          >
                            <NavigationArrowIcon size={14} />
                            {t('profile.openInMaps')}
                          </Link>
                        )}
                      </div>
                    </LayerCard.Primary>
                  </LayerCard>
                )}

                <LayerCard>
                  <LayerCard.Primary>
                    <div>
                      <label className="text-xs font-medium text-kumo-subtle block mb-1">
                        {t('profile.roles')}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {roles.length > 0 ? (
                          roles.map((role) => (
                            <Badge key={role} variant="primary">
                              {role}
                            </Badge>
                          ))
                        ) : (
                          <Text size="sm">{'—'}</Text>
                        )}
                      </div>
                    </div>
                  </LayerCard.Primary>
                </LayerCard>
              </>
            )}
          </div>
        )}

        {activeTab === 'facePhoto' && (
          <div className="flex flex-col gap-6">
            {loading && (
              <div className="flex items-center justify-center py-16">
                <Loader size="lg" />
              </div>
            )}

            {!loading && notLinked && (
              <Empty
                icon={<UserIcon size={48} className="text-kumo-inactive" />}
                title={t('myProfile.notLinkedTitle')}
                description={t('myProfile.notLinkedDescription')}
              />
            )}

            {!loading && !notLinked && error && (
              <Banner variant="error">
                {t('common.errorPrefix')} {error}
              </Banner>
            )}

            {!loading && !notLinked && !error && coworker && photoHandlers && (
              <LayerCard>
                <LayerCard.Primary>
                  <div className="mb-4">
                    <RouterLink to={RETAKE_PHOTO_PATH}>
                      <Button variant="primary" icon={<CameraIcon size={16} />}>
                        {t('faceStatus.retake.button')}
                      </Button>
                    </RouterLink>
                  </div>
                  <CoworkerFacePhotoSection handlers={photoHandlers} />
                </LayerCard.Primary>
              </LayerCard>
            )}
          </div>
        )}

        {activeTab === 'preferences' && (
          <LayerCard>
            <LayerCard.Primary>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-kumo-subtle block mb-1.5">
                    {t('profile.language')}
                  </label>
                  <Select
                    className="w-40"
                    data-testid="language-select"
                    value={locale}
                    onValueChange={(v) => {
                      if (v) setLocale(v as Locale)
                    }}
                  >
                    <Select.Option value="pt-BR">Portugues (Brasil)</Select.Option>
                    <Select.Option value="en">English</Select.Option>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-kumo-subtle block mb-1.5">
                    {t('profile.appearance')}
                  </label>
                  <Select
                    className="w-40"
                    data-testid="appearance-select"
                    value={darkMode ? 'dark' : 'light'}
                    onValueChange={(v) => {
                      if (v === 'dark' && !darkMode) onToggleDarkMode()
                      if (v === 'light' && darkMode) onToggleDarkMode()
                    }}
                  >
                    <Select.Option value="light">{t('profile.appearance.light')}</Select.Option>
                    <Select.Option value="dark">{t('profile.appearance.dark')}</Select.Option>
                  </Select>
                </div>
              </div>
            </LayerCard.Primary>
          </LayerCard>
        )}
      </div>
    </div>
  )
}
